import { useMemo, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { MdPhotoLibrary } from "react-icons/md";
import GalleryModal from "@/Component/Module/HolidayDetail/GalleryModal";
import { NO_IMAGE_URL } from "@/Component/Module/HolidayDetail/utils/packageGallery";
import appendLoopingSlickDots from "@/Common/LoopingSlickDots";
import styles from "./ActivityDetail.module.css";

function GalleryNavArrow({ direction, onClick }) {
  return (
    <button
      type="button"
      className={`${styles.galleryNav} ${
        direction === "prev" ? styles.galleryNavPrev : styles.galleryNavNext
      }`}
      onClick={onClick}
      aria-label={direction === "prev" ? "Previous images" : "Next images"}
    >
      {direction === "prev" ? <BsChevronLeft size={18} /> : <BsChevronRight size={18} />}
    </button>
  );
}

function handleImageError(event) {
  if (event.currentTarget.src === NO_IMAGE_URL) return;
  event.currentTarget.src = NO_IMAGE_URL;
}

function GalleryTile({ image, badge, onClick, className = "", showIcon = false }) {
  const showBadge = Boolean(badge) || showIcon;

  return (
    <button type="button" className={`${styles.tile} ${className}`} onClick={onClick}>
      <img src={image || NO_IMAGE_URL} alt="" onError={handleImageError} />
      {showBadge ? (
        <span className={styles.badge}>
          {showIcon ? <MdPhotoLibrary aria-hidden="true" /> : null}
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function getImageSrc(item) {
  return String(item?.src || item?.imagePath || item?.path || "").trim();
}

function getActivityThumbImage(images = []) {
  const list = Array.isArray(images) ? images : [];

  const byType = list.find((img) =>
    /thumb/i.test(String(img.imageType || img.imageTypeName || ""))
  );
  if (byType) return getImageSrc(byType);

  const byFrontFlag = list.find((img) => Number(img.isFrontImage) === 2);
  if (byFrontFlag) return getImageSrc(byFrontFlag);

  if (list[1]) return getImageSrc(list[1]);

  return getImageSrc(list[0]);
}

function buildActivityPreviewTiles(images = [], centerImage = "") {
  const sources = images.map((img) => getImageSrc(img)).filter(Boolean);
  const main = centerImage || sources[0] || "";
  const others = sources.filter((src) => src !== main);
  const fallback = others[0] || main;
  const at = (index) => others[index] || fallback;

  return {
    leftTop: at(0) || NO_IMAGE_URL,
    leftBottom: at(2) || NO_IMAGE_URL,
    main: main || NO_IMAGE_URL,
    rightTop: at(1) || NO_IMAGE_URL,
    rightBottom: at(3) || NO_IMAGE_URL,
  };
}

function buildActivityGalleryCategories(images = []) {
  const mapped = (Array.isArray(images) ? images : [])
    .map((img, index) => {
      const src = String(img?.imagePath || img?.path || img || "").trim();
      if (!src) return null;
      return {
        id: `activity-${index}-${src}`,
        src,
        alt: img?.alternatetext || img?.alternateText || "",
        category: "activity",
      };
    })
    .filter(Boolean);

  return {
    all: mapped,
    activity: mapped,
    property: [],
    clientReview: [],
  };
}

export default function ActivityGallery({ title, images = [] }) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");

  const categories = useMemo(() => buildActivityGalleryCategories(images), [images]);
  const count = categories.all.length;
  const thumbImage = useMemo(() => getActivityThumbImage(images), [images]);

  const preview = useMemo(
    () => buildActivityPreviewTiles(categories.all, thumbImage),
    [categories.all, thumbImage]
  );

  const openGallery = (imageSrc = "") => {
    setModalImageSrc(imageSrc);
    setGalleryOpen(true);
  };

  const lastPreviewIndex = Math.min(4, Math.max(count - 1, 0));

  const sliderItems = count
    ? categories.all.slice(0, 5).map((item, index) => ({
        key: `slide-${index}`,
        image: item.src,
        badge: index === lastPreviewIndex ? `View All (${count})` : "",
        showIcon: index === lastPreviewIndex,
      }))
    : [
        {
          key: "empty",
          image: NO_IMAGE_URL,
          badge: "View All (0)",
          showIcon: true,
        },
      ];

  const sliderSettings = {
    dots: sliderItems.length > 2,
    infinite: sliderItems.length > 2,
    speed: 450,
    slidesToShow: Math.min(2, sliderItems.length),
    slidesToScroll: 1,
    arrows: sliderItems.length > 2,
    // prevArrow: sliderItems.length > 2 ? <GalleryNavArrow direction="prev" /> : null,
    // nextArrow: sliderItems.length > 2 ? <GalleryNavArrow direction="next" /> : null,
    dotsClass: `slick-dots ${styles.carouselDots}`,
    appendDots: appendLoopingSlickDots(`slick-dots ${styles.carouselDots}`),
  };

  return (
    <>
      <div className={styles.galleryShell}>
      <div className={styles.galleryDesktop}>
        <div className={styles.sideCol}>
          <GalleryTile
            image={preview.leftTop}
            onClick={() => openGallery(preview.leftTop)}
          />
          <GalleryTile
            image={preview.leftBottom}
            onClick={() => openGallery(preview.leftBottom)}
          />
        </div>

        <GalleryTile
          className={styles.mainTile}
          image={preview.main}
          onClick={() => openGallery(preview.main)}
        />

        <div className={styles.sideCol}>
          <GalleryTile
            image={preview.rightTop}
            onClick={() => openGallery(preview.rightTop)}
          />
          <GalleryTile
            image={preview.rightBottom}
            badge={`View All (${count})`}
            showIcon
            onClick={() => openGallery(preview.rightBottom)}
          />
        </div>
      </div>

      <div className={styles.galleryMobile}>
        <div className={styles.gallerySliderWrap}>
          <Slider {...sliderSettings}>
            {sliderItems.map((item) => (
              <div key={item.key} className={styles.gallerySliderSlide}>
                <GalleryTile
                  image={item.image}
                  badge={item.badge}
                  showIcon={item.showIcon}
                  onClick={() => openGallery(item.image)}
                />
              </div>
            ))}
          </Slider>
        </div>
      </div>
      </div>

      <GalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title={title}
        categories={categories}
        initialCategory="all"
        initialImageSrc={modalImageSrc}
        hideCategoryTabs
      />
    </>
  );
}
